import { type ReactNode } from 'react';
import type { Creator } from '@/types';
import {
  useSanctionCreator,
  useSoftDeleteCreator,
} from '../hooks/use-creator-mutations';
import { CreatorFields } from './creator-fields';
import { CreatorEditForm } from './creator-edit-form';
import { CreatorDeleteConfirm } from './creator-delete-confirm';
import { CreatorSanctionConfirm } from './creator-sanction-confirm';

export type DetailMode =
  | 'view'
  | 'edit'
  | 'delete'
  | 'restore'
  | 'hard-delete'
  | 'sanction'
  | 'alert'
  | 'appeal'
  | 'user-delete';

type SetMode = (m: DetailMode) => void;
type SectionProps = {
  creator: Creator;
  setMode: SetMode;
  onClose: () => void;
};

export function BodyView({
  creator,
  mode,
  set,
}: {
  creator: Creator;
  mode: DetailMode;
  set: SetMode;
}): ReactNode {
  if (mode === 'edit')
    return (
      <CreatorEditForm
        creator={creator}
        onSave={() => set('view')}
        onCancel={() => set('view')}
      />
    );
  return <CreatorFields creator={creator} />;
}

export function DeleteSection({
  creator,
  setMode,
  onClose,
}: SectionProps): ReactNode {
  const del = useSoftDeleteCreator();
  return (
    <div className="mt-base">
      <CreatorDeleteConfirm
        onConfirm={(reason) =>
          del.mutate({ id: creator.id, reason }, { onSuccess: onClose })
        }
        onCancel={() => setMode('view')}
        isPending={del.isPending}
      />
    </div>
  );
}

function isRedeemed(creator: Creator): boolean {
  const c = creator.expand?.collector;
  return !!c?.has_used_redemption && !!c?.redemption_passed_at;
}

function buildSanctionSubmit(
  id: string,
  mutate: ReturnType<typeof useSanctionCreator>['mutate'],
  onClose: () => void
) {
  return (vars: { reason: string; notes: string }): void => {
    mutate({ id, ...vars }, { onSuccess: onClose });
  };
}

export function SanctionSection({
  creator,
  setMode,
  onClose,
}: SectionProps): ReactNode {
  const sanction = useSanctionCreator();
  return (
    <div className="mt-base">
      <CreatorSanctionConfirm
        collectorUsername={creator.expand?.collector?.ig_username}
        collectorRedeemed={isRedeemed(creator)}
        onConfirm={buildSanctionSubmit(creator.id, sanction.mutate, onClose)}
        onCancel={() => setMode('view')}
        isPending={sanction.isPending}
      />
    </div>
  );
}

export {
  AppealSection,
  UserDeleteSection,
} from './creator-detail-fase-b-sections';
